// 套系工作流配置页 — suite-generation-workflow task 4
//
// 运营「填表一键」跑完套系底版生成:
//   同主体 (subject_prompt) × 多场景变体 (scene_variants) → 套系 LoRA 出图
//   → 统一换成一张参考脸 (reference_face_url, 生产 PuLID/ReActor)
//   → 落候选 (plate-generation-runs)。
// 候选 → 选入套系 → 上架 复用现有「底版标框」页 (/content/:id/plates), 本页只引导跳转。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Space,
  Alert,
  Typography,
  Upload,
  Tag,
  Divider,
  Result,
  message,
} from 'antd';
import {
  ThunderboltOutlined,
  UploadOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  PictureOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { listPackages, type ContentPackage } from '@/api/content';
import { listModels, type LoRAModel } from '@/api/lora';
import { triggerPlateAutogen, type PlateAutogenEngine } from '@/api/plates';
import { uploadReferenceFace } from '@/api/uploads';

const { Text, Paragraph } = Typography;

/** 一次运行总张数上限保护 (与后端约定的软上限一致)。 */
const MAX_TOTAL = 40;

interface SubmittedRun {
  runId: number;
  packageId: number;
  count: number;
  engine: string;
}

export function SuiteWorkflowPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [appId, setAppId] = useState<number>(3); // 3 = photo-studio (与内容套系页默认一致)
  const [packages, setPackages] = useState<ContentPackage[]>([]);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [loraModels, setLoraModels] = useState<LoRAModel[]>([]);

  const [uploading, setUploading] = useState(false);
  const [refFaceUrl, setRefFaceUrl] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<SubmittedRun | null>(null);

  // 拉某 app 的套系包 (供选择) + 已训好的 LoRA 模型 (供快速填 lora_name/触发词)。
  // 套系搜索走服务端 q (ILIKE): app 下套系可达数千, 只拉一页做客户端过滤会
  // 搜不到排名靠后的套系 (2026-08-09 "搜北京暂无数据" 根因)。
  const pkgSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const searchPackages = useCallback((aid: number, q: string) => {
    if (!aid || Number.isNaN(aid)) return;
    setPkgLoading(true);
    listPackages({ app_id: aid, q: q || undefined, page_size: 50 })
      .then((r) => setPackages(r.data))
      .catch((e) => message.error(e instanceof Error ? e.message : '加载套系失败'))
      .finally(() => setPkgLoading(false));
  }, []);
  const loadForApp = useCallback((aid: number) => {
    if (!aid || Number.isNaN(aid)) return;
    searchPackages(aid, '');
    listModels(aid)
      .then((r) => setLoraModels(r.data ?? []))
      .catch(() => setLoraModels([])); // LoRA 列表失败不阻断, 运营可手填
  }, [searchPackages]);
  const onPkgSearch = (q: string) => {
    if (pkgSearchTimer.current) clearTimeout(pkgSearchTimer.current);
    pkgSearchTimer.current = setTimeout(() => searchPackages(appId, q.trim()), 300);
  };

  useEffect(() => {
    loadForApp(appId);
  }, [appId, loadForApp]);

  const packageOptions = useMemo(
    () =>
      packages.map((p) => ({
        value: p.id,
        label: `${p.name} (#${p.id} · ${p.code})`,
      })),
    [packages],
  );

  const loraOptions = useMemo(
    () =>
      loraModels
        .filter((m) => m.status === 'ready')
        .map((m) => ({ value: m.name, label: `${m.name}${m.trigger_word ? ` · ${m.trigger_word}` : ''}`, model: m })),
    [loraModels],
  );

  // 选一个已训好的 LoRA → 自动回填 lora_name + 触发词 (仍可手改)。
  const onPickLora = (name: string) => {
    const m = loraModels.find((x) => x.name === name);
    form.setFieldsValue({
      lora_name: name,
      lora_trigger: m?.trigger_word ?? form.getFieldValue('lora_trigger'),
    });
  };

  // 参考脸上传: 走后端中转 → OSS → 签名读 URL, 回填表单。
  const uploadProps: UploadProps = {
    accept: 'image/*',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      setUploading(true);
      uploadReferenceFace(file as File)
        .then(({ url }) => {
          setRefFaceUrl(url);
          form.setFieldsValue({ reference_face_url: url });
          message.success('参考脸已上传');
        })
        .catch((e) => message.error(e instanceof Error ? e.message : '上传失败'))
        .finally(() => setUploading(false));
      return false; // 阻止 antd 自行上传, 用我们的中转
    },
  };

  const onFinish = async (v: Record<string, unknown>) => {
    const packageId = Number(v.package_id);
    if (!packageId) {
      message.warning('请选择套系包');
      return;
    }
    const subjectPrompt = String(v.subject_prompt ?? '').trim();
    if (!subjectPrompt) {
      message.warning('请填写主体描述 (subject_prompt)');
      return;
    }
    const sceneVariants = ((v.scene_variants as string[] | undefined) ?? [])
      .map((s) => (s ?? '').trim())
      .filter(Boolean);
    if (sceneVariants.length === 0) {
      message.warning('请至少添加一个场景变体');
      return;
    }
    const perSceneCount = Number(v.per_scene_count ?? 2);
    const total = sceneVariants.length * perSceneCount;
    if (total > MAX_TOTAL) {
      message.error(`总张数 ${total} 超上限 ${MAX_TOTAL} (场景数 × 每场景张数), 请减少场景或每场景张数`);
      return;
    }

    const engine = (v.engine as PlateAutogenEngine) ?? 'onething';
    const referenceFaceURL = String(v.reference_face_url ?? refFaceUrl ?? '').trim();

    setSubmitting(true);
    try {
      const r = await triggerPlateAutogen(packageId, {
        // 后端 autogen 仍要求非空 prompt; 用 subject_prompt 兜底 (后端展开时优先用
        // subject_prompt + scene_variants)。TODO: 后端从 subject_prompt 派生 prompt 后可去掉。
        prompt: subjectPrompt,
        engine,
        subject_prompt: subjectPrompt,
        scene_variants: sceneVariants,
        per_scene_count: perSceneCount,
        reference_face_url: referenceFaceURL || undefined,
        lora_name: (v.lora_name as string) || undefined,
        lora_weight: v.lora_weight != null ? Number(v.lora_weight) : undefined,
        lora_trigger: (v.lora_trigger as string) || undefined,
        negative_prompt: (v.negative_prompt as string) || undefined,
      });
      setSubmitted({ runId: r.run_id, packageId, count: r.count, engine: r.engine });
      message.success(`已提交套系工作流 (run #${r.run_id}), 生成异步进行`);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 提交成功后的引导页: 跳到现有候选可视化 (选入/标框/上架都在那)。
  if (submitted) {
    return (
      <Result
        status="success"
        title={`套系工作流已提交 · run #${submitted.runId}`}
        subTitle={
          <span>
            引擎 <Tag color="geekblue">{submitted.engine}</Tag> · 计划出图 {submitted.count} 张。
            生成异步进行, 候选会落到该套系包的「生成记录」批次 #{submitted.runId}。
            去候选可视化展开批次, 挑选满意底版「选入套系」→ 标框 → 套系上架。
          </span>
        }
        extra={[
          <Button
            type="primary"
            key="candidates"
            icon={<ArrowRightOutlined />}
            onClick={() => navigate(`/content/${submitted.packageId}/plates`)}
          >
            去候选 / 选入 / 上架
          </Button>,
          <Button
            key="again"
            onClick={() => {
              setSubmitted(null);
            }}
          >
            再配一个套系
          </Button>,
        ]}
      />
    );
  }

  return (
    <div style={{ maxWidth: 820 }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="统一脸 = 套系样片模特脸"
        description="本页生成的一致脸底版 = 前台浏览的套系审美主线 (同一个模特, 对标创想熊套系样片)。用户付费时会再把这张模特脸换成用户自己的脸 — 两者互不影响。参考脸留空则生成 clean 无脸底版。"
      />

      <Card title="套系工作流配置" size="small">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            engine: 'onething',
            lora_weight: 0.7,
            per_scene_count: 2,
            scene_variants: [''],
          }}
        >
          {/* App + 套系包 */}
          <Space size={16} align="start" wrap>
            <Form.Item label="App ID" tooltip="切换后重新拉该 App 的套系包与 LoRA 模型">
              <InputNumber
                min={1}
                value={appId}
                onChange={(val) => setAppId(typeof val === 'number' ? val : 1)}
                style={{ width: 140 }}
                placeholder="3 = photo-studio"
              />
            </Form.Item>
            <Form.Item
              name="package_id"
              label="套系包"
              rules={[{ required: true, message: '请选择套系包' }]}
              style={{ minWidth: 360 }}
            >
              <Select
                loading={pkgLoading}
                showSearch
                filterOption={false}
                onSearch={onPkgSearch}
                notFoundContent={pkgLoading ? '搜索中…' : '无匹配套系'}
                placeholder="输入套系名搜索"
                options={packageOptions}
                style={{ width: 360 }}
              />
            </Form.Item>
            {/* 常驻入口: 历史生成批次/候选 都在该套系的底版页(选入/标框/上架同处) */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.package_id !== cur.package_id}>
              {({ getFieldValue }) => {
                const pid = Number(getFieldValue('package_id'));
                return pid ? (
                  <Button type="link" onClick={() => navigate(`/content/${pid}/plates`)}>
                    查看该套系的生成记录与候选 →
                  </Button>
                ) : null;
              }}
            </Form.Item>
          </Space>

          <Divider orientation="left" plain>
            引擎 & 套系 LoRA
          </Divider>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="LoRA 与参考脸换脸仅在 engine=onething 时生效 (即梦不吃 LoRA / 不走自建换脸)。"
          />
          <Space size={16} wrap align="start">
            <Form.Item name="engine" label="引擎">
              <Select
                style={{ width: 200 }}
                options={[
                  { value: 'onething', label: 'onething (LoRA + 换脸, 推荐)' },
                  { value: 'jimeng', label: '即梦 (国风底版, 无 LoRA/换脸)' },
                ]}
              />
            </Form.Item>
            <Form.Item label="快速选 LoRA" tooltip="从已训好的 LoRA 模型带出 lora_name + 触发词">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: 240 }}
                placeholder="选已训好的 LoRA (可选)"
                options={loraOptions}
                onChange={(val) => val && onPickLora(val)}
              />
            </Form.Item>
          </Space>
          <Space size={16} wrap align="start">
            <Form.Item name="lora_name" label="lora_name">
              <Input placeholder="如 gfcxx_v1" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="lora_weight" label="lora_weight">
              <InputNumber min={0} max={2} step={0.05} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="lora_trigger" label="lora_trigger (触发词)">
              <Input placeholder='如 "gfcxx style"' style={{ width: 200 }} />
            </Form.Item>
          </Space>

          <Divider orientation="left" plain>
            主体 & 场景
          </Divider>
          <Form.Item
            name="subject_prompt"
            label="主体描述 subject_prompt (中文)"
            rules={[{ required: true, message: '请填写主体描述' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="如: 一位现代中国女性穿华丽戏曲戏服精致珠花头饰, 温柔微笑"
            />
          </Form.Item>

          <Form.List name="scene_variants">
            {(fields, { add, remove }) => (
              <Form.Item
                label="场景变体 scene_variants (每行一个中文短语)"
                tooltip="每个场景会与主体描述拼成一条完整 prompt, 同主体游多场景"
                style={{ marginBottom: 12 }}
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
                      <Form.Item
                        {...field}
                        noStyle
                        rules={[{ required: true, whitespace: true, message: '场景不能为空' }]}
                      >
                        <Input
                          placeholder="如: 在大本钟前 / 在伦敦塔桥前 / 在伦敦眼前"
                          style={{ width: 520 }}
                        />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add('')} icon={<PlusOutlined />} style={{ width: 200 }}>
                    添加场景
                  </Button>
                </Space>
              </Form.Item>
            )}
          </Form.List>

          <Space size={16} wrap align="start">
            <Form.Item
              name="per_scene_count"
              label="每场景张数 per_scene_count"
              tooltip={`总张数 = 场景数 × 每场景张数, 上限 ${MAX_TOTAL}`}
            >
              <InputNumber min={1} max={MAX_TOTAL} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {() => {
                const scenes = ((form.getFieldValue('scene_variants') as string[] | undefined) ?? []).filter(
                  (s) => (s ?? '').trim(),
                );
                const per = Number(form.getFieldValue('per_scene_count') ?? 0);
                const total = scenes.length * per;
                return (
                  <div style={{ paddingTop: 30 }}>
                    <Text type={total > MAX_TOTAL ? 'danger' : 'secondary'}>
                      预计总张数: {total} 张 (≤ {MAX_TOTAL})
                    </Text>
                  </div>
                );
              }}
            </Form.Item>
          </Space>

          <Divider orientation="left" plain>
            参考脸 (可选)
          </Divider>
          <Form.Item
            name="reference_face_url"
            label="参考脸 URL (reference_face_url)"
            tooltip="清晰正脸最佳。空则生成无脸底版 (clean plate)。可上传, 也可直接粘贴一张 OSS 签名读 URL。"
          >
            <Input
              placeholder="上传后自动回填, 或粘贴 OSS 签名读 URL; 留空 = 生成无脸底版"
              onChange={(e) => setRefFaceUrl(e.target.value)}
              allowClear
            />
          </Form.Item>
          <Space align="start" size={16} wrap style={{ marginBottom: 8 }}>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                上传参考脸
              </Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12 }}>
              提示: 上传一张清晰正脸; 本次生成的每张底版都会换成这张脸 → 统一模特脸样片。
            </Text>
          </Space>
          {refFaceUrl && (
            <div style={{ marginBottom: 8 }}>
              <img
                src={refFaceUrl}
                alt="参考脸预览"
                style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}

          <Divider />
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            <PictureOutlined /> 提交后异步生成 → 候选落该套系包「生成记录」批次 → 去候选可视化「选入套系」→ 标框 → 上架。选入/标框/上架复用现有 <Text code>底版标框</Text> 页。
          </Paragraph>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<ThunderboltOutlined />}
              loading={submitting}
            >
              提交套系工作流
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
